import { useDispatch, useSelector } from 'react-redux';
import { getActiveSlicers } from '../../common/redux/selectors/viz-selectors';
import {
  useDiscoverBooleanOptionSelector,
  useDiscoverOptionSelector,
} from '../../discovery/discovery-context/discovery.context';
import { useCallback, useMemo, MouseEvent } from 'react';
import { IDrillContext } from '../../discovery/viz-redirect';
import _, { get, isFunction } from 'lodash';
import {
  AllowedDashletDrillLinkDynamicFields,
  getDashboardFilters,
  getDynamicValues,
  stripDashletSuffix,
  VIZ_OPTION_IDS,
} from '../../common';
import DiscoverActions from '../../common/redux/actions/DiscoverActions';
import {
  useCustomFormatToggleSelector,
  useHiddenFieldWarnings,
  useOpenDiscoveryPresentStateSelector,
  useOpenVizChartSpec,
  useOpenVizSelector,
  useVizOptionSelector,
  useWithSugarContextFilters,
} from '../../common/redux/selectors/viz-selector.hook';
import { ChartSpecs } from '../../discovery/ChartSpecs';
import { IToggle, IViz } from '../../discovery';
import { Viz } from '../../discovery/VizUtil';
import { EventKeyType } from './mobile-viz-footer.interfaces';
import { isDashletUser as isDashletUserSelector } from '../../common/redux/selectors/AccountSelectors';
import Discover from '../../common/redux/actions/DiscoverActions';
import { IDashletFilter } from '../../common/utilities/sugar-filter-converter/sugar-filter-converter.interfaces';
import { useFiscalCalendarInfo } from '../../common/utilities/fiscal-info.hook';

export const useCanCollapse = (discoveryId: string) => {
  const chartSpec = useOpenVizChartSpec({ discoveryId });
  const isForecastChanges = chartSpec?.id === ChartSpecs.pipeline_changes.id;

  const canCollapseToggle: IToggle = useCustomFormatToggleSelector({
    discoveryId,
    toggleName: 'canCollapse',
    defaultValue: false,
  });
  const canCollapse = !!canCollapseToggle?.on;

  return {
    isForecastChanges,
    canCollapseEnabled: canCollapse,
  };
};

export const useDashletExternalLinkAction = (vizId: string) => {
  const { tenantId, tenantIdmId } = useSelector(
    (state: { main: any }) => state?.main,
  );
  const slicers = useSelector(state =>
    getActiveSlicers(state, { discoveryId: vizId }),
  );

  const viz = useOpenVizSelector({ discoveryId: vizId });
  const filters = Viz.getFiltersFromViz(viz);

  const { value: slicerSelections } = useDiscoverOptionSelector({
    vizId,
    option: VIZ_OPTION_IDS.slicerSelections,
  });

  const runtimeFilters = useVizOptionSelector({
    discoveryId: vizId,
    option: VIZ_OPTION_IDS.runtimeFilters,
  });

  const dashboardFilters: IDashletFilter = getDashboardFilters();
  const fiscalCalendarInfo = useFiscalCalendarInfo({
    discoveryId: vizId,
  });

  return useCallback(
    (event: MouseEvent<Element, MouseEvent>) => {
      event.preventDefault();
      event.stopPropagation();
      const queryParams = {
        tenant_id: tenantId,
        tenant_idm_id: tenantIdmId,
      };

      // just populate drill-linked report with slicer info
      const drillContext: IDrillContext = {
        linkToReport: {
          id: stripDashletSuffix(vizId),
        },
        filters,
        runtimeFilters,
        dashboardFilters,
        slicers,
        slicerSelections,
        fiscalCalendarInfo,
      };

      const messageBody = {
        drillContext,
        linkStrategy: 'clear',
      };

      const dynamicValues = _.pick(
        getDynamicValues(),
        AllowedDashletDrillLinkDynamicFields,
      );

      if (_.isObject(messageBody?.drillContext)) {
        messageBody.drillContext.dynamicValues = dynamicValues;
      }

      DiscoverActions.postWindowOpenReport(queryParams, messageBody);
    },
    [
      filters,
      runtimeFilters,
      dashboardFilters,
      slicerSelections,
      slicers,
      tenantId,
      tenantIdmId,
      vizId,
      fiscalCalendarInfo,
    ],
  );
};

export const useMobileVizFooterState = ({ vizId }) => {
  const { value: showLegendPanel, updateValue: setShowLegendPanel } =
    useDiscoverBooleanOptionSelector({
      option: VIZ_OPTION_IDS.showLegendPanel,
      defaultValue: true,
    });
  const { value: showFiltersPanel, updateValue: setShowFiltersPanel } =
    useDiscoverBooleanOptionSelector({
      option: VIZ_OPTION_IDS.showFiltersPanel,
      defaultValue: true,
    });
  const { isMobile, isDashletMode, isDashletUser } = useSelector(
    (state: any) => {
      return {
        isDashletUser: isDashletUserSelector(state.account),
        isMobile: get(state, 'main.isMobile', false),
        isDashletMode: get(state, 'dashlet.isDashletMode', false),
      };
    },
  );

  const discovery = useOpenDiscoveryPresentStateSelector({
    discoveryId: vizId,
  });

  const convertedFilters = useWithSugarContextFilters({
    discoveryId: vizId,
  });

  const vizWithFilters: IViz = useMemo(
    () => ({
      ...discovery.viz,
      options: {
        ...discovery.viz.options,
        filters: JSON.stringify(convertedFilters),
      },
    }),
    [convertedFilters, discovery.viz],
  );

  const { isBlocked } = useHiddenFieldWarnings({ discoveryId: vizId });

  const dispatch = useDispatch();
  const refresh = useCallback(() => {
    if (isBlocked) {
      return;
    }
    const variables = Viz.buildQueryVariables(vizWithFilters);
    dispatch(
      Discover.vizQuery({
        id: vizId,
        variables: {
          ...variables,
          bypassCache: true,
        },
      }),
    );
  }, [dispatch, isBlocked, vizId, vizWithFilters]);

  const { isForecastChanges, canCollapseEnabled } = useCanCollapse(vizId);

  const toggleCanCollapse = useCallback(() => {
    if (isForecastChanges) {
      dispatch(
        Discover.setCustomFormatToggle(
          vizId,
          !canCollapseEnabled,
          'canCollapse',
        ),
      );
    }
  }, [canCollapseEnabled, dispatch, isForecastChanges, vizId]);

  const queryResults = _.get(discovery, 'vizQueryResults.data');
  const chartSpec = ChartSpecs[discovery.viz.chartType];
  const chartIsValid =
    isFunction(chartSpec?.validate) && chartSpec.validate(discovery.viz).valid;
  const isRefreshDisabled = _.isEmpty(queryResults) || !chartIsValid;

  const onMenuSelection = useCallback(
    (eventKey: EventKeyType) => {
      switch (eventKey) {
        case 'HIDE_LEGEND': {
          setShowLegendPanel(!showLegendPanel);
          // force a resize event when closing the panel to give anything on the screen a chance to account for it
          sendResizeEvent();
          break;
        }
        case 'HIDE_FILTERS': {
          setShowFiltersPanel(!showFiltersPanel);
          // force a resize event when closing the panel to give anything on the screen a chance to account for it
          sendResizeEvent();
          break;
        }
        case 'REFRESH': {
          refresh();
          // force a resize event when closing the panel to give anything on the screen a chance to account for it
          sendResizeEvent();
          break;
        }
        case 'COLLAPSE_WHITESPACE': {
          toggleCanCollapse();
          // force a resize event when closing the panel to give anything on the screen a chance to account for it
          sendResizeEvent();
          break;
        }
        default: {
          console.warn(`${eventKey} is not yet supported`);
        }
      }
    },
    [
      refresh,
      setShowFiltersPanel,
      setShowLegendPanel,
      showFiltersPanel,
      showLegendPanel,
      toggleCanCollapse,
    ],
  );

  const retVal = useMemo(
    () => ({
      onMenuSelection,
      showLegendPanel,
      showFiltersPanel,
      isMobile,
      isDashletMode,
      isDashletUser,
      isRefreshDisabled,
    }),
    [
      isDashletMode,
      isMobile,
      onMenuSelection,
      showFiltersPanel,
      isDashletUser,
      showLegendPanel,
      isRefreshDisabled,
    ],
  );
  return retVal;
};

const sendResizeEvent = () => {
  setTimeout(() => window.dispatchEvent(new window.Event('resize')), 100);
};
